import dayjs from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { generateUniqueDatabaseId, isSameData } from '../dto/data-model-utils.js';
import { CustomerProfile } from './customer-profile.js';
import { Product } from './product.js';
import { TrueTime } from '../../time/true-time.js';
import { TimeZone } from '../../time/timezone.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { LegacyTicketVoucher } from './legacy-ticket-voucher.js';
import { OnlineProductOrder } from './online-product-order.js';

/**
 * @deprecated
 */
export class LegacyTicketGiftCard extends LegacyTicketVoucher {
  public static readonly TYPE_ID: string = '3e87432897a44e3581a3a2c084e9ad64';

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly orderId: string,
    public readonly promoCode: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly product: Product,
    public readonly customer: CustomerProfile,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyTicketGiftCard.TYPE_ID;
  }

  static fromProductOfOrder(order: OnlineProductOrder, product: Product): LegacyTicketGiftCard {
    return new LegacyTicketGiftCard(
        generateUniqueDatabaseId(),
        order.businessRef.clone(),
        order.uniqueId,
        LegacyTicketGiftCard.generatePromoCode(),
        TrueTime.now(),
        product.clone(),
        order.customer.clone(),
    );
  }

  static fromOrder(order: OnlineProductOrder, itemIndex: number): LegacyTicketGiftCard {
    return new LegacyTicketGiftCard(
        generateUniqueDatabaseId(),
        order.businessRef.clone(),
        order.uniqueId,
        LegacyTicketGiftCard.generatePromoCode(),
        TrueTime.now(),
        order.items[itemIndex].product.clone(),
        order.customer.clone(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyTicketGiftCard {
    return new LegacyTicketGiftCard(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'orderId'),
        MapUtils.get<string>(map, 'promoCode'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.fromData<Product>(MapUtils.get<SerializedData>(map, 'product')),
        context.fromData<CustomerProfile>(MapUtils.get<SerializedData>(map, 'customer')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'orderId': this.orderId,
      'promoCode': this.promoCode,
      'creationDate': context.encodeDate(this.creationDate),
      'product': context.toData(this.product),
      'productId': this.product.uniqueId,
      'customer': context.toData(this.customer),
      'customerId': this.customer.uniqueId,
    };
  }

  clone(): this {
    return new LegacyTicketGiftCard(
        this.uniqueId,
        this.businessRef.clone(),
        this.orderId,
        this.promoCode,
        TimeZone.cloneDate(this.creationDate),
        this.product.clone(),
        this.customer.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.orderId === this.orderId &&
      other.promoCode === this.promoCode &&
      other.creationDate === this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      isSameData(other.product, this.product) &&
      isSameData(other.customer, this.customer);
  }

  static generatePromoCode(): string {
    const randomStr = generateUniqueDatabaseId({ length: 15, alphabet: 'UC_LETTERS_DIGITS' });
    return `GC-${randomStr.substring(0, 5)}-${randomStr.substring(5, 10)}-${randomStr.substring(10, randomStr.length)}`;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof LegacyTicketGiftCard;
  }

  static assertType(order: DataModel): order is LegacyTicketGiftCard {
    return order instanceof LegacyTicketGiftCard;
  }
}

export class LegacyTicketGiftCardBuilder extends DataModelBuilder<LegacyTicketGiftCard> {
  build(context: SerializationContext, map: SerializedData): LegacyTicketGiftCard {
    return LegacyTicketGiftCard.fromMap(context, map);
  }
}
